// Source attribution for signups.
//
// The static SEO landing pages (public/*.html) link into the SPA at
// `/app?src=<slug>`. We stash that slug so it survives the tailor session +
// the OTP sign-in roundtrip (which lands on a different route without the
// param), and SignIn reads it back into the `signup_complete` event + the
// server-side signup event. Mirrors the existing `dr_qr_source` rail used for
// QR-code attribution, so paid/organic landing-page signups attribute to the
// page that drove them instead of collapsing to "direct".
const SRC_KEY = 'dr_src';

/** Lowercase, strip to [a-z0-9_-], cap at 64 chars. Same shape as the qr code. */
export function sanitizeSourceCode(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 64);
}

/** Pull a sanitized `src` value out of a query string. Returns '' if absent. */
export function readSourceParam(search: string): string {
  return sanitizeSourceCode(new URLSearchParams(search).get('src') || '');
}

/** Persist a landing source so it survives the OTP roundtrip. No-op if empty. */
export function storeSource(code: string): void {
  if (!code) return;
  try { localStorage.setItem(SRC_KEY, code); } catch { /* private mode / quota */ }
}

/** Read the persisted landing source (or '' if none / storage blocked). */
export function getStoredSource(): string {
  try { return localStorage.getItem(SRC_KEY) || ''; } catch { return ''; }
}

/**
 * Capture `?src=` from the current URL into storage on app entry. Idempotent:
 * re-running with the same param just re-writes the same value.
 */
export function captureSourceFromLocation(search: string = window.location.search): void {
  storeSource(readSourceParam(search));
}

/**
 * Resolve the source label to attach to a signup. Priority: an explicit `?src`
 * on the sign-in URL (e.g. a referral share link) wins, then the landing-page
 * src we stored on app entry, then the QR-scan label (prefixed so it stays
 * distinguishable). Returns '' when nothing is known.
 */
export function resolveSignupSource(urlSrc: string, storedSrc: string, storedQr: string): string {
  if (urlSrc) return urlSrc;
  if (storedSrc) return storedSrc;
  return storedQr ? `qr-${storedQr}` : '';
}
